import { FC, useEffect } from 'react'

import { useTranslation } from 'react-i18next'

import { observer } from 'mobx-react-lite'

import type { CreateObjectDraftStepId } from 'features/kyrgyzstan/create-object/types'

import {
  CreateObjectStepId,
  createObjectSessionStore,
} from 'entities/kyrgyzstan/model/create-object'

import {
  createObjectDraftStepComponentMap,
  createObjectObjectTypeStepComponent,
  createObjectStepOrder,
  createObjectStepTotal,
  getCreateObjectStepById,
} from './model'

interface CreateObjectWizardProps {
  stepId: string
  onExit: () => void
  onOpenStep: (stepId: CreateObjectStepId) => void
  onSubmitted: () => void
}

export const CreateObjectWizard: FC<CreateObjectWizardProps> = observer(
  ({ stepId, onExit, onOpenStep, onSubmitted }) => {
    const { t } = useTranslation()
    const activeStep = getCreateObjectStepById(stepId)
    const draft = createObjectSessionStore.draft
    const draftId = createObjectSessionStore.draftId

    useEffect(() => {
      if (!activeStep) {
        onOpenStep(createObjectStepOrder[0])

        return
      }

      if (activeStep.requiresDraft && !draftId) {
        onOpenStep(CreateObjectStepId.ObjectType)
      }
    }, [activeStep, draftId, onOpenStep])

    if (!activeStep) {
      return null
    }

    const handleBack = () => {
      if (activeStep.previousStepId) {
        onOpenStep(activeStep.previousStepId)

        return
      }

      onExit()
    }

    const handleNext = () => {
      if (activeStep.nextStepId) {
        onOpenStep(activeStep.nextStepId)

        return
      }

      onSubmitted()
    }

    const layoutProps = {
      onBack: handleBack,
      progressLabel: t('kyrgyzstan_block.create_object.progress.step', {
        current: activeStep.index,
        total: createObjectStepTotal,
      }),
    }

    if (activeStep.id === CreateObjectStepId.ObjectType) {
      const StepComponent = createObjectObjectTypeStepComponent

      return (
        <StepComponent
          layoutProps={layoutProps}
          draft={draft}
          setDraft={createObjectSessionStore.setDraft}
          onNext={handleNext}
        />
      )
    }

    if (!draftId) {
      return null
    }

    const StepComponent =
      createObjectDraftStepComponentMap[
        activeStep.id as CreateObjectDraftStepId
      ]

    return (
      <StepComponent
        layoutProps={layoutProps}
        draftId={draftId}
        onNext={handleNext}
        onOpenStep={onOpenStep}
      />
    )
  },
)
